import { Minus, Package, Plus, ShoppingBag, Trash2, Truck, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Link, useNavigate } from "react-router-dom";
import { formatBRL, useCart } from "@/hooks/cart/CartProvider";
import { getPackageInfoForItem } from "@/lib/shippingDefaults";

const CartDrawer = () => {
  const { items, totals, isOpen, closeCart, removeItem, setQuantity } = useCart();
  const navigate = useNavigate();

  const goToCheckout = () => {
    closeCart();
    navigate("/checkout");
  };

  return (
    <Sheet open={isOpen} onOpenChange={(o) => (o ? null : closeCart())}>
      <SheetContent side="right" className="flex w-full flex-col p-0 sm:max-w-md">
        <SheetHeader className="border-b border-border px-5 py-4">
          <SheetTitle className="flex items-center gap-2 font-display text-xl">
            <ShoppingBag className="h-5 w-5 text-primary" />
            Seu carrinho
            {totals.itemsCount > 0 && (
              <span className="ml-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                {totals.itemsCount}
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              <ShoppingBag className="h-7 w-7 text-muted-foreground" />
            </div>
            <p className="font-display text-lg text-foreground">Seu carrinho está vazio</p>
            <p className="text-sm text-muted-foreground">
              Cada compra ajuda a financiar o resgate de animais.
            </p>
            <Button onClick={closeCart} className="mt-2">Continuar comprando</Button>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1">
              <ul className="divide-y divide-border">
                {items.map((item) => (
                  <li key={item.key} className="flex gap-3 px-5 py-4">
                    <Link to={item.productSlug ? `/produto/${item.productSlug}` : "#"} onClick={closeCart} className="shrink-0">
                      <img
                        src={item.image}
                        alt={item.productName}
                        className="h-20 w-20 rounded-md border border-border object-cover"
                      />
                    </Link>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="flex items-start justify-between gap-2">
                        <Link
                          to={item.productSlug ? `/produto/${item.productSlug}` : "#"}
                          onClick={closeCart}
                          className="line-clamp-2 text-sm font-medium text-foreground hover:text-primary"
                        >
                          {item.productName}
                        </Link>
                        <button
                          aria-label="Remover"
                          onClick={() => removeItem(item.key)}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      {item.variantLabel && (
                        <p className="mt-0.5 text-xs text-muted-foreground">{item.variantLabel}</p>
                      )}
                      <ShippingInfo item={item} />
                      <div className="mt-auto flex items-center justify-between pt-2">
                        <div className="flex items-center rounded-md border border-border">
                          <button
                            className="flex h-8 w-8 items-center justify-center text-muted-foreground hover:text-foreground"
                            onClick={() => setQuantity(item.key, item.quantity - 1)}
                            aria-label="Diminuir"
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <span className="min-w-[2rem] text-center text-sm font-medium">{item.quantity}</span>
                          <button
                            className="flex h-8 w-8 items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-40"
                            onClick={() => setQuantity(item.key, item.quantity + 1)}
                            disabled={item.maxStock != null && item.quantity >= item.maxStock}
                            aria-label="Aumentar"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <span className="text-sm font-semibold text-foreground">
                          {formatBRL(item.unitPrice * item.quantity)}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </ScrollArea>

            <div className="border-t border-border bg-card px-5 py-4">
              <ShippingSummary items={items} />
              <Separator className="my-3" />
              {totals.discount > 0 && (
                <div className="mb-1 flex justify-between text-xs text-muted-foreground">
                  <span>Você economiza</span>
                  <span className="font-medium text-accent">{formatBRL(totals.discount)}</span>
                </div>
              )}
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-muted-foreground">Subtotal</span>
                <span className="font-display text-2xl font-bold text-foreground">
                  {formatBRL(totals.subtotal)}
                </span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Frete e cupom serão calculados no checkout.
              </p>
              <Button className="mt-3 w-full" size="lg" onClick={goToCheckout}>
                Finalizar compra
              </Button>
              <button
                onClick={closeCart}
                className="mt-2 w-full text-center text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                <X className="mr-1 inline h-3 w-3" /> Continuar comprando
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

function ShippingInfo({ item }: { item: import("@/types/cart").CartItem }) {
  const info = getPackageInfoForItem(item);
  const { weight, length, width, height } = info.dimensions;
  const weightStr = weight.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 3 });
  return (
    <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground/80">
      <Package className="h-3 w-3 shrink-0" />
      <span className="truncate">
        {info.ruleLabel} · {weightStr} kg · {length}×{width}×{height} cm
      </span>
    </p>
  );
}

function ShippingSummary({ items }: { items: import("@/types/cart").CartItem[] }) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-foreground">
        <Truck className="h-3.5 w-3.5 text-primary" />
        <span>Resumo do frete</span>
      </div>
      <ul className="space-y-1.5">
        {items.map((item) => {
          const info = getPackageInfoForItem(item);
          const { weight, length, width, height } = info.dimensions;
          const weightStr = weight.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 3 });
          return (
            <li key={item.key} className="flex items-start justify-between gap-2 text-[11px]">
              <span className="line-clamp-1 text-muted-foreground">{item.productName}</span>
              <span className="shrink-0 text-muted-foreground/90">
                {info.ruleLabel} · {weightStr} kg · {length}×{width}×{height} cm
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default CartDrawer;
